import { HttpClient } from '../../protocols/http/httpClient'
import { SpeType } from '../../../domain/models/SpeModel'
import { IUpdateSpe, SPE } from '../../../domain/usecases/SPE'
import { validateHttpStatus } from '../../../validation/validateHttpResponse'

export class RemoteUpdateSpe implements IUpdateSpe {
  constructor(
    private readonly url: string,
    private readonly httpClient: HttpClient
  ) {}

  @validateHttpStatus
  async updateOne(params: SPE.UpdateParams): Promise<SpeType> {
    const {
      proxies = [],
      commercial_board_document,
      ...properties
    } = this.formatDataToBackend(params)

    const formData = new FormData()

    if (proxies.length) {
      for (let i = 0; i < proxies.length; i++) {
        if (proxies[i].id) {
          formData.append(`proxies[${i}][id]`, proxies[i].id)
        }
        formData.append(`proxies[${i}][name]`, proxies[i].name)
        formData.append(`proxies[${i}][email]`, proxies[i].email)
        formData.append(`proxies[${i}][cpf]`, proxies[i].cpf)
      }
    }

    const props: Record<string, any> = properties
    for (const key in props) {
      if (props[key] instanceof Date) {
        // Handle Date objects - convert to ISO string
        formData.append(key, props[key].toISOString())
      } else if (typeof props[key] === 'object' && props[key] !== null) {
        for (const subKey in props[key]) {
          const value = props[key][subKey]
          // null/undefined virariam as strings "null"/"undefined" no banco
          // (ex.: address.complement nullable). Sem a chave o backend mantém.
          if (value === null || value === undefined) continue
          formData.append(`${key}[${subKey}]`, value)
        }
      } else if (props[key] === null && key === 'cri_signature_date') {
        // Send empty string for nullable date fields to clear them
        formData.append(key, '')
      } else if (props[key] !== undefined && props[key] !== null) {
        formData.append(key, props[key])
      }
    }

    if (commercial_board_document) {
      formData.append('commercial_board_document', commercial_board_document)
    }

    const response = await this.httpClient.patch?.({
      url: `${this.url}/spes/${properties.id}`,
      data: formData,
      headers: {
        Accept: '*/*'
      }
    })
    return response
  }

  private formatDataToBackend(params: SPE.UpdateParams) {
    return {
      id: params.id,
      cnpj: params.cnpj,
      company_id: params.companyId,
      business_name: params.business_name,
      address: {
        zip_code: params.zip_code,
        address: params.address,
        number: params.number,
        district: params.district,
        city: params.city,
        state: params.state,
        country: params.country,
        complement: params.complement
      },
      nire: params.nire,
      commercial_board_name: params.commercial_board_name,
      commercial_board_identifier: params.commercial_board_identifier,
      commercial_board_document: params.commercial_board_document_url,
      proxies: params.proxies,
      bank_account: {
        type: params.bank_account_type,
        bank: params.bank,
        branch_number: params.branch,
        branch_digit: params.branch_digit,
        account_number: params.account,
        account_digit: params.account_digit
      },
      bank_billet_preference: {
        fine: params.fine,
        interest: params.interest,
        interest_type: params.interest_type
      },
      cri_signature_date: params.cri_signature_date,
      uau_empresa_code: params.uau_empresa_code
    }
  }
}
